import { MenuItem, MenuConditionContext, UserRole } from '@/types/menu'

/**
 * 按用户角色过滤菜单（递归）
 *
 * @param menus 菜单配置数组
 * @param userRole 用户角色
 * @param conditionCtx 附加过滤上下文（用于 MenuItem.condition），可选
 * @returns 过滤后的菜单数组
 */
export function filterMenuByRole(
  menus: MenuItem[],
  userRole: UserRole,
  conditionCtx?: MenuConditionContext
): MenuItem[] {
  return menus
    .filter((item) => {
      if (!item.roles.includes(userRole)) return false
      if (item.condition) {
        // 没提供 ctx 时（保持向后兼容）按通过处理
        if (!conditionCtx) return true
        return item.condition(conditionCtx)
      }
      return true
    })
    .map((item) => {
      if (item.children) {
        return {
          ...item,
          children: filterMenuByRole(item.children, userRole, conditionCtx),
        }
      }
      return item
    })
}

/**
 * 按「用户的多个角色」过滤菜单（并集）：菜单项 roles 与用户任一角色相交即可见。
 * 用于多角色用户（如 ADMIN+ENGINEER、ENGINEER+SERVICE）**同时**看到各角色的菜单，
 * 避免按单一 roles[0] 过滤导致看不全/被锁在某个角色。
 */
export function filterMenuByRoles(
  menus: MenuItem[],
  userRoles: UserRole[],
  conditionCtx?: MenuConditionContext
): MenuItem[] {
  return menus
    .filter((item) => {
      if (!item.roles.some((r) => userRoles.includes(r))) return false
      if (item.condition) {
        if (!conditionCtx) return true
        return item.condition(conditionCtx)
      }
      return true
    })
    .map((item) => {
      if (item.children) {
        return {
          ...item,
          children: filterMenuByRoles(item.children, userRoles, conditionCtx),
        }
      }
      return item
    })
}

/**
 * 扁平化菜单树为一维数组
 *
 * @param menus 菜单配置数组
 * @returns 扁平化后的菜单数组
 */
export function flattenMenu(menus: MenuItem[]): MenuItem[] {
  return menus.reduce((acc, item) => {
    acc.push(item)
    if (item.children) {
      acc.push(...flattenMenu(item.children))
    }
    return acc
  }, [] as MenuItem[])
}

/**
 * 根据路径查找菜单项
 *
 * @param menus 菜单配置数组
 * @param path 路由路径
 * @returns 找到的菜单项，未找到返回 null
 */
export function findMenuByPath(menus: MenuItem[], path: string): MenuItem | null {
  const flattened = flattenMenu(menus)
  return flattened.find((item) => item.href === path) || null
}

/**
 * 生成面包屑导航数组（支持动态路由）
 *
 * @param menus 菜单配置数组
 * @param path 当前路由路径
 * @returns 面包屑数组（从根到当前页面）
 */
export function getBreadcrumbs(menus: MenuItem[], path: string): MenuItem[] {
  // 1. 尝试精确匹配（原有逻辑）
  const exactMatch = findExactPath(menus, path)
  if (exactMatch.length > 0) return exactMatch

  // 2. 前缀匹配 + 动态追加（支持详情页、创建页等）
  return findBestMatchWithDynamic(menus, path)
}

/**
 * 精确路径匹配（原getBreadcrumbs的内部逻辑）
 */
function findExactPath(menus: MenuItem[], path: string): MenuItem[] {
  const breadcrumbs: MenuItem[] = []

  function findPath(items: MenuItem[], targetPath: string, parents: MenuItem[] = []): boolean {
    for (const item of items) {
      if (item.href === targetPath) {
        breadcrumbs.push(...parents, item)
        return true
      }

      if (item.children) {
        const found = findPath(item.children, targetPath, [...parents, item])
        if (found) return true
      }
    }
    return false
  }

  findPath(menus, path)
  return breadcrumbs
}

/**
 * 智能前缀匹配并追加动态路径段
 */
function findBestMatchWithDynamic(menus: MenuItem[], pathname: string): MenuItem[] {
  const segments = pathname.split('/').filter(Boolean)

  // 从最长路径开始匹配
  for (let i = segments.length; i > 0; i--) {
    const testPath = '/' + segments.slice(0, i).join('/')
    const match = findExactPath(menus, testPath)

    if (match.length > 0) {
      // 找到基础匹配，追加动态部分
      const remaining = segments.slice(i)
      const dynamicItems: MenuItem[] = []

      remaining.forEach((segment, index) => {
        const prevPath = i + index === 0 ? testPath : dynamicItems[index - 1]?.href || testPath
        const currentPath = prevPath + '/' + segment

        // UUID格式检测 → "详情"
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
          dynamicItems.push({
            id: `dynamic-detail-${index}`,
            title: '详情',
            href: currentPath,
            roles: [],
          })
          return
        }

        // 关键词映射
        const keywordMap: Record<string, string> = {
          create: '创建',
          edit: '编辑',
          assign: '分配',
          dispatch: '派单',
          orders: '订单',
          customers: '客户',
          info: '信息',
          lab: '实验室',
        }

        if (keywordMap[segment]) {
          dynamicItems.push({
            id: `dynamic-${segment}`,
            title: keywordMap[segment],
            href: currentPath,
            roles: [],
          })
        }
      })

      return [...match, ...dynamicItems]
    }
  }

  return []
}

/**
 * 工作台相关的路径前缀（这些路径的子页面应该让"工作台"菜单高亮）
 * 注意：这些路径不是独立的菜单项，而是各角色工作台页面的子路由
 */
const DASHBOARD_WORKSPACE_PREFIXES = [
  '/dashboard/engineer/',  // 工程师工作台子页面（如任务详情）
  '/dashboard/market/',    // 市场部工作台子页面（排除已有菜单的路径在下方处理）
  '/dashboard/service/',   // 客服部工作台子页面
  '/dashboard/finance/',   // 财务部工作台子页面
  '/dashboard/monthly-company/', // 月结公司工作台子页面
]

/**
 * 已有独立菜单项的路径前缀（这些不应该让"工作台"高亮，而是让各自的菜单项高亮）
 */
const INDEPENDENT_MENU_PREFIXES = [
  '/dashboard/orders',
  '/dashboard/market/exceptions',
  '/dashboard/service/certificates',
  '/dashboard/service/mail-tracking',
  '/dashboard/finance/payments',
  '/dashboard/finance/invoices',
  '/dashboard/finance/monthly',
  '/dashboard/finance/refunds',
  '/dashboard/config',
  '/dashboard/customer',
  '/dashboard/base-data',
  '/dashboard/notifications',
  '/dashboard/audit-logs',
]

/**
 * 检查路径是否匹配菜单项（支持前缀匹配）
 *
 * @param menuPath 菜单路径
 * @param currentPath 当前路由路径
 * @returns 是否匹配
 */
export function isPathActive(menuPath: string | undefined, currentPath: string): boolean {
  if (!menuPath || menuPath === '#') return false

  // 规范化路径（去除尾部斜杠）
  const normalizedMenuPath = menuPath.replace(/\/$/, '')
  const normalizedCurrentPath = currentPath.replace(/\/$/, '')

  // 精确匹配
  if (normalizedMenuPath === normalizedCurrentPath) return true

  // 特殊处理工作台（/dashboard）的匹配逻辑
  if (normalizedMenuPath === '/dashboard') {
    // 检查是否是工作台相关的子页面
    const isWorkspaceSubPage = DASHBOARD_WORKSPACE_PREFIXES.some(prefix =>
      normalizedCurrentPath.startsWith(prefix)
    )

    // 检查是否已经有独立的菜单项匹配
    const hasIndependentMenu = INDEPENDENT_MENU_PREFIXES.some(prefix =>
      normalizedCurrentPath.startsWith(prefix)
    )

    // 只有当是工作台子页面且没有独立菜单时，才让工作台高亮
    return isWorkspaceSubPage && !hasIndependentMenu
  }

  // 前缀匹配（适用于其他菜单项）
  // 例如：菜单项 /dashboard/orders 应该匹配 /dashboard/orders/123
  if (normalizedCurrentPath.startsWith(normalizedMenuPath + '/')) {
    return true
  }

  return false
}

/**
 * 检查菜单项是否包含当前路径（用于自动展开父菜单）
 *
 * @param item 菜单项
 * @param currentPath 当前路由路径
 * @returns 是否包含当前路径
 */
export function menuContainsPath(item: MenuItem, currentPath: string): boolean {
  // 检查自身
  if (isPathActive(item.href, currentPath)) return true

  // 检查子菜单
  if (item.children) {
    return item.children.some((child) => menuContainsPath(child, currentPath))
  }

  return false
}

/**
 * 检查路径是否是菜单中的精确匹配路径
 *
 * @param menus 菜单配置数组
 * @param path 当前路由路径
 * @returns 是否是菜单中的精确路径
 */
export function isMenuPath(menus: MenuItem[], path: string): boolean {
  const flattened = flattenMenu(menus)
  const normalizedPath = path.replace(/\/$/, '')
  return flattened.some((item) => item.href?.replace(/\/$/, '') === normalizedPath)
}

/**
 * 角色工作台路径前缀（非菜单但角色可访问的子路由）
 */
// 注意：前缀不带尾斜杠——下方匹配用 `tabPath === prefix || tabPath.startsWith(prefix + '/')`，
// 带尾斜杠会变成 `//` 双斜杠导致工程师自身任务标签 /dashboard/engineer/tasks/x 被误判无权限。
const ROLE_WORKSPACE_PREFIXES: Partial<Record<UserRole, string[]>> = {
  MARKET: ['/dashboard/market'],
  SERVICE: ['/dashboard/service'],
  ENGINEER: ['/dashboard/engineer'],
  LAB_ENGINEER: ['/dashboard/engineer'],
  FINANCE: ['/dashboard/finance'],
  MONTHLY_ADMIN: ['/dashboard/monthly-company'],
}

/**
 * 检查标签路径是否对指定角色可访问
 *
 * @param tabPath 标签的路径
 * @param role 目标角色，null 表示管理员（可见所有）
 * @param menus 完整菜单配置
 * @returns 是否可访问
 */
export function isTabAccessibleForRole(
  tabPath: string,
  role: UserRole | null,
  menus: MenuItem[]
): boolean {
  // 管理员视角可见所有标签
  if (!role) return true

  // 首页和个人中心始终可见
  if (tabPath === '/dashboard' || tabPath === '/dashboard/profile') return true

  // 获取角色可见的菜单路径（排除 /dashboard，它只做精确匹配，不做前缀匹配）
  const filteredMenu = filterMenuByRole(menus, role)
  const menuPaths = flattenMenu(filteredMenu)
    .map((item) => item.href)
    .filter((href): href is string => !!href && href !== '/dashboard')

  // 合并菜单路径 + 工作台前缀
  const allPrefixes = [...menuPaths, ...(ROLE_WORKSPACE_PREFIXES[role] || [])]

  return allPrefixes.some(
    (prefix) => tabPath === prefix || tabPath.startsWith(prefix + '/')
  )
}
